import { Button } from "@car/ui/components/button";
import { ArrowLeft02Icon, CheckmarkCircle02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { createFileRoute } from "@tanstack/react-router";

import { openConsult } from "@/lib/consult";
import { MobileNavigation, SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { loadSiteContent, pageSections, serviceCards, useSiteContent } from "@/lib/content";
import { pageCrumbs, pageMeta, pageTitle } from "@/lib/seo";

import { publicPageHeaders } from "./-public-cache";

export const Route = createFileRoute("/about")({
  // already in the cache from the root route; here for the page's title and description
  loader: ({ context }) => loadSiteContent(context.queryClient),
  head: ({ loaderData }) => {
    const title = loaderData?.aboutPage.title ?? "درباره ما";
    return {
      meta: [
        ...pageMeta(pageTitle(title), loaderData?.aboutPage.metaDescription),
        pageCrumbs(title, "/about"),
      ],
    };
  },
  headers: publicPageHeaders,
  component: AboutPage,
});

type Row = { heading: string; paragraphs: string[] };

/** متن صفحه به ردیف: هر زیرعنوان با بندهای زیرش؛ بندهای پیش از اولین زیرعنوان ردیفی بی‌عنوان‌اند */
function bodyRows(body: string) {
  const rows: Row[] = [];
  for (const section of pageSections(body)) {
    if (section.kind === "heading" || rows.length === 0) {
      rows.push({ heading: section.kind === "heading" ? section.text : "", paragraphs: [] });
    }
    if (section.kind === "paragraph") rows.at(-1)?.paragraphs.push(section.text);
  }
  return rows;
}

/** «عنوان: متن» در آغاز بند (زیرمجموعه‌ها، ارزش‌ها)؛ عنوانش پررنگ می‌آید */
const RUN_IN = /^([^:.،؛]{2,32}):\s+(.+)$/s;

/**
 * «درباره ما» از بلوک مدیریت محتوا: شعار برند به خط نمایشی، کمانِ پل زیر آن، و متن صفحه به شکل
 * ردیف‌های خط‌دار (زیرعنوان سرِ خواندن، متن روبه‌رویش) به‌جای کارت؛ خدمات زیرمجموعه‌ها پیش از آن
 * به شکل ستون‌های شماره‌دار با فهرست خدمات هر یک.
 */
function AboutPage() {
  const page = useSiteContent().aboutPage;
  const services = serviceCards(page.services);
  return (
    <main className="min-h-[100dvh] bg-background pb-20 text-foreground md:pb-0">
      <SiteHeader />
      <article>
        <header className="home-container pt-10 sm:pt-16">
          <div className="home-head">
            <nav aria-label="مسیر صفحه" className="text-sm text-muted-foreground">
              <a href="/" className="hover:text-foreground">
                خانه
              </a>
              <span aria-hidden="true" className="mx-2">
                /
              </span>
              <span aria-current="page" className="font-bold text-foreground">
                {page.title}
              </span>
            </nav>
            <h1 className="home-title max-w-[16ch]">{page.tagline || page.title}</h1>
            {page.lede ? <p className="home-lede text-pretty">{page.lede}</p> : null}
            {page.updated ? (
              <p className="text-xs font-bold text-muted-foreground">{page.updated}</p>
            ) : null}
          </div>
          <div className="about-bridge" aria-hidden="true" />
        </header>

        {services.length > 0 ? (
          <section aria-labelledby="about-services" className="home-container about-services">
            <h2 id="about-services" className="about-services-title">
              {page.servicesTitle}
            </h2>
            <ol className="about-services-list">
              {services.map((service, index) => (
                <li key={index} className="about-service">
                  <div className="flex items-baseline gap-3">
                    <span className="about-service-number" aria-hidden="true">
                      {(index + 1).toLocaleString("fa-IR", { minimumIntegerDigits: 2 })}
                    </span>
                    <h3 className="about-service-name">{service.name}</h3>
                  </div>
                  {service.tagline ? (
                    <p className="about-service-tagline">{service.tagline}</p>
                  ) : null}
                  {service.text.map((text, at) => (
                    <p key={at} className="about-service-text">
                      {text}
                    </p>
                  ))}
                  {service.items.length > 0 ? (
                    <ul className="about-service-items">
                      {service.items.map((item, at) => (
                        <li key={at}>
                          <HugeiconsIcon
                            icon={CheckmarkCircle02Icon}
                            strokeWidth={1.8}
                            aria-hidden="true"
                            className="size-5 shrink-0 text-primary"
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        <div className="home-container">
          {bodyRows(page.body).map((row, index) => (
            <section key={index} className="about-row">
              {row.heading ? <h2 className="about-row-title">{row.heading}</h2> : null}
              <div className="about-row-text">
                {row.paragraphs.map((text, at) => {
                  const runIn = RUN_IN.exec(text);
                  return runIn ? (
                    <p key={at}>
                      <strong>{runIn[1]}</strong>
                      {runIn[2]}
                    </p>
                  ) : (
                    <p key={at}>{text}</p>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <footer className="home-container about-close">
          {page.closing ? <p className="about-close-line">{page.closing}</p> : null}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <Button size="lg" aria-haspopup="dialog" onClick={openConsult}>
              مشاوره رایگان
            </Button>
            <a href="/contact" className="home-link">
              تماس و شعب
              <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.8} className="size-5" />
            </a>
          </div>
        </footer>
      </article>
      <SiteFooter />
      <MobileNavigation />
    </main>
  );
}
